import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useLang } from "@/lib/lang";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function LeadModal({ open, onOpenChange, source = "popup" }) {
  const { t } = useLang();
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [audience, setAudience] = useState("india");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!open) {
      setDone(false);
      setPhone("");
      setName("");
      setAudience("india");
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!phone || phone.trim().length < 6) {
      toast.error("Please enter a valid phone number");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", { phone: phone.trim(), name: name.trim() || null, audience, source });
      setDone(true);
      toast.success("Thanks! We'll call you within 24 hours.");
      setTimeout(() => onOpenChange(false), 1600);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="lead-modal" className="max-w-md p-0 overflow-hidden border-0 rounded-3xl" style={{ background: "var(--bg)" }}>
        <div className="p-8">
          <div className="section-eyebrow mb-3">{t.lead_modal.badge}</div>
          <DialogHeader className="text-left">
            <DialogTitle className="font-display text-3xl leading-tight" style={{ color: "var(--primary)" }}>
              {t.lead_modal.title}
            </DialogTitle>
            <DialogDescription className="mt-2 text-base" style={{ color: "var(--text-secondary)" }}>
              {t.lead_modal.sub}
            </DialogDescription>
          </DialogHeader>

          {done ? (
            <div data-testid="lead-modal-thanks" className="mt-6 rounded-2xl p-6 text-center" style={{ background: "var(--bg-paper)" }}>
              <div className="text-2xl font-display" style={{ color: "var(--primary)" }}>ਸ਼ੁਕਰੀਆ! 🙏</div>
              <p className="mt-2 text-sm" style={{ color: "var(--text-secondary)" }}>
                {t.contact.thanks}
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest font-semibold" style={{ color: "var(--text-secondary)" }}>{t.lead_modal.phone}</label>
                <Input
                  data-testid="lead-modal-phone"
                  type="tel"
                  autoFocus
                  required
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+91 98xxx xxxxx"
                  className="mt-1.5 h-12 rounded-xl text-base"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest font-semibold" style={{ color: "var(--text-secondary)" }}>{t.lead_modal.name}</label>
                <Input
                  data-testid="lead-modal-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Simran / Harpreet"
                  className="mt-1.5 h-12 rounded-xl"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest font-semibold" style={{ color: "var(--text-secondary)" }}>{t.lead_modal.audience}</label>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {["india", "canada", "usa"].map((v) => (
                    <button
                      type="button"
                      key={v}
                      data-testid={`lead-audience-${v}`}
                      onClick={() => setAudience(v)}
                      className={`py-2.5 rounded-xl text-sm font-semibold border transition-colors ${audience === v ? "text-white" : ""}`}
                      style={{
                        borderColor: audience === v ? "var(--primary)" : "var(--border-strong)",
                        background: audience === v ? "var(--primary)" : "transparent",
                        color: audience === v ? "white" : "var(--primary)",
                      }}
                    >
                      {t.lead_modal[v]}
                    </button>
                  ))}
                </div>
              </div>
              <div className="pt-2 flex items-center gap-3">
                <button data-testid="lead-modal-submit" disabled={submitting} className="btn-primary flex-1 h-12 disabled:opacity-60">
                  {submitting ? "..." : t.lead_modal.submit}
                </button>
                <button type="button" data-testid="lead-modal-later" onClick={() => onOpenChange(false)} className="text-sm font-medium px-2" style={{ color: "var(--text-secondary)" }}>
                  {t.lead_modal.later}
                </button>
              </div>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
